import { sortRowsForRelatedDisplay } from "./relatedRowOrdering";

export interface DisplayGroup<T> {
  name: string;
  sortOrder: number;
  items: T[];
}

export interface QrsJumpKitLocation {
  locationName: string;
  locationOrder: number;
  itemOrder: number;
}

const UNASSIGNED_LOCATION: QrsJumpKitLocation = {
  locationName: "Unassigned Location",
  locationOrder: 900,
  itemOrder: 900
};

const qrsJumpKitLocationEntries: Array<[string, QrsJumpKitLocation]> = [
  ...location("Top Outer Compartment (Red Pouch)", 10, [
    "cat_commercial-tourniquet",
    "cat_occlusive-dressing-3-x-4",
    "cat_roller-gauze-2",
    "cat_roller-gauze-3",
    "cat_roller-gauze-4"
  ]),
  ...location("Top Outer Compartment - Assessment", 20, [
    "cat_bp-cuff-adult",
    "cat_bp-cuff-pediatric",
    "cat_stethoscope-dual",
    "cat_pulse-oximeter",
    "cat_digital-thermometer-non-tympanic",
    "cat_penlight",
    "cat_bandage-shears",
    "cat_nemsis-compliant-epcr-program"
  ]),
  ...location("Bottom Outer Compartment - Medications", 30, [
    "cat_naloxone",
    "cat_instant-glucose-45g",
    "cat_syringe-epinephrine-administration-kit",
    "cat_electronic-glucose-meter",
    "cat_glucose-test-strips",
    "cat_lancets",
    "cat_alcohol-prep-pads",
    "cat_gauze-2x2",
    "cat_band-aids-narcan",
    "cat_gloves-medical-ppe",
    "cat_n95-respirator",
    "cat_glasses-narcan"
  ]),
  ...location("Main Compartment - Top Layer", 40, [
    "cat_trauma-dressing-10-x-30",
    "cat_sterile-burn-sheet-4-x-4"
  ]),
  ...location("Blue Pouch", 50, [
    "cat_sterile-gauze-pads-4-x-4",
    "cat_sterile-gauze-pads-3-x-3",
    "cat_cold-pack-chemical",
    "cat_chemical-heat-pack",
    "cat_band-aids"
  ]),
  ...location("Green Pouch", 60, [
    "cat_self-adhering-wrap",
    "cat_elastic-bandage-2",
    "cat_elastic-bandage-4",
    "cat_adhesive-tape-2",
    "cat_adhesive-tape-2-hypoallergenic",
    "cat_adhesive-tape-1"
  ]),
  ...location("Yellow Pouch", 70, [
    "cat_triangular-bandage",
    "cat_sam-splint"
  ]),
  ...location("Main Compartment", 80, [
    "cat_sharps-container",
    "cat_red-biohazard-bags",
    "cat_bp-cuff-child",
    "cat_bp-cuff-thigh-large",
    "cat_bp-cuff-adult-small",
    "cat_pediatric-length-based-dosing-sizing-tape"
  ])
];

const qrsJumpKitLocationByCatalogItemId = new Map(qrsJumpKitLocationEntries);

const airwayBagLocationEntries: Array<[string, QrsJumpKitLocation]> = [
  ...location("Side Compartment - Ventilation", 10, [
    "cat_adult-bvm-device",
    "cat_pediatric-bvm-device",
    "cat_pocket-mask-with-one-way-valve",
    "cat_adult-bvm-mask",
    "cat_child-bvm-mask",
    "cat_infant-bvm-mask",
    "cat_neonatal-pediatric-care-bvm-mask"
  ]),
  ...location("Main Compartment - Center", 20, [
    "cat_portable-oxygen-cylinder",
    "cat_non-sparking-o2-wrench-tank-opening-device"
  ]),
  ...location("Elastic Pocket - Airway Adjuncts (OPA)", 30, [
    "cat_oropharyngeal-airway-opa-size-0",
    "cat_oropharyngeal-airway-opa-size-1",
    "cat_oropharyngeal-airway-opa-size-2",
    "cat_oropharyngeal-airway-opa-size-3",
    "cat_oropharyngeal-airway-opa-size-4",
    "cat_oropharyngeal-airway-opa-size-5"
  ]),
  ...location("Elastic Pocket - Airway Adjuncts (NPA)", 40, [
    "cat_nasopharyngeal-airway-npa-size-16",
    "cat_nasopharyngeal-airway-npa-size-24",
    "cat_nasopharyngeal-airway-npa-size-26",
    "cat_nasopharyngeal-airway-npa-size-32",
    "cat_nasopharyngeal-airway-npa-size-34",
    "cat_water-soluble-lubricant"
  ]),
  ...location("Elastic Pocket - Suction Adjuncts", 50, [
    "cat_flexible-suction-catheter-6-fr",
    "cat_flexible-suction-catheter-8-fr",
    "cat_flexible-suction-catheter-10-fr",
    "cat_flexible-suction-catheter-12-fr",
    "cat_flexible-suction-catheter-14-fr",
    "cat_flexible-suction-catheter-16-fr",
    "cat_bulb-syringe"
  ]),
  ...location("Elastic Pocket - Oxygen Delivery (Adult NRB)", 60, [
    "cat_adult-high-concentration-mask-nrb"
  ]),
  ...location("Elastic Pocket - Oxygen Delivery (Adult NC)", 70, [
    "cat_adult-nasal-cannula"
  ]),
  ...location("Elastic Pocket - Oxygen Delivery (Pediatric NC)", 80, [
    "cat_pediatric-nasal-cannula"
  ]),
  ...location("Elastic Pocket - Oxygen Delivery (Pediatric & Infant NRB)", 90, [
    "cat_pediatric-high-concentration-mask-nrb",
    "cat_infant-high-concentration-mask-nrb"
  ]),
  ...location("Other", 100, [
    "cat_spare-oxygen-cylinder"
  ])
];

const airwayBagLocationByCatalogItemId = new Map(airwayBagLocationEntries);

function location(locationName: string, locationOrder: number, catalogItemIds: string[]): Array<[string, QrsJumpKitLocation]> {
  return catalogItemIds.map((catalogItemId, index) => [
    catalogItemId,
    {
      locationName,
      locationOrder,
      itemOrder: index + 1
    }
  ]);
}

function sortItemsAlphabetically<T extends { itemName: string }>(items: T[]): T[] {
  return sortRowsForRelatedDisplay(items);
}

export function isQrsJumpKitName(name: string | null | undefined): boolean {
  const normalized = (name ?? "").trim().toLowerCase();
  return normalized === "qrs jump kit" || normalized === "qrs bag";
}

export function isAirwayBagName(name: string | null | undefined): boolean {
  return (name ?? "").trim().toLowerCase() === "airway bag";
}

export function isObKitName(name: string | null | undefined): boolean {
  return (name ?? "").trim().toLowerCase() === "ob kit";
}

export function getQrsJumpKitLocation(catalogItemId: string | null | undefined): QrsJumpKitLocation {
  if (!catalogItemId) return UNASSIGNED_LOCATION;
  return qrsJumpKitLocationByCatalogItemId.get(catalogItemId) ?? UNASSIGNED_LOCATION;
}

export function getAirwayBagLocation(catalogItemId: string | null | undefined): QrsJumpKitLocation {
  if (!catalogItemId) return UNASSIGNED_LOCATION;
  return airwayBagLocationByCatalogItemId.get(catalogItemId) ?? UNASSIGNED_LOCATION;
}

export function groupItemsForBagDisplay<T extends { catalogItemId: string | null; category: string | null; itemName: string }>(
  bagName: string | null | undefined,
  items: T[]
): DisplayGroup<T>[] {
  if (isObKitName(bagName)) {
    return items.length > 0
      ? [{ name: "", sortOrder: 0, items: sortItemsAlphabetically(items) }]
      : [];
  }

  const getDisplayLocation = isQrsJumpKitName(bagName)
    ? getQrsJumpKitLocation
    : isAirwayBagName(bagName)
      ? getAirwayBagLocation
      : null;

  if (getDisplayLocation) {
    const groups = new Map<string, DisplayGroup<T>>();
    for (const item of items) {
      const displayLocation = getDisplayLocation(item.catalogItemId);
      const group = groups.get(displayLocation.locationName) ?? {
        name: displayLocation.locationName,
        sortOrder: displayLocation.locationOrder,
        items: []
      };
      group.items.push(item);
      groups.set(displayLocation.locationName, group);
    }

    return [...groups.values()]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .map((group) => ({
        ...group,
        items: sortItemsAlphabetically(group.items)
      }));
  }

  const groups = new Map<string, DisplayGroup<T>>();
  for (const item of items) {
    const name = item.category ?? "Other";
    const group = groups.get(name) ?? {
      name,
      sortOrder: 0,
      items: []
    };
    group.items.push(item);
    groups.set(name, group);
  }

  return [...groups.values()]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((group) => ({
      ...group,
      items: sortItemsAlphabetically(group.items)
    }));
}

export const __qrsJumpKitLocationTestIds = Object.freeze([...qrsJumpKitLocationByCatalogItemId.keys()]);
export const __airwayBagLocationTestIds = Object.freeze([...airwayBagLocationByCatalogItemId.keys()]);
